/**
 * CE QU'ON RECUEILLE AU TÉLÉPHONE EN CONFIRMANT UN RENDEZ-VOUS — module PUR.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── La demande (Inès, 05/10/2026) ──────────────────────────────────────────
 *
 * « Quand on confirme un rendez-vous, il faut mentionner les infos qu'on a
 * récoltées du patient au moment du coup de fil. Nom, prénom, ddn, et surtout
 * heure et endroit du rendez-vous (labo ou à domicile), le cas échéant
 * l'adresse. Il doit y avoir un pop-up qui surgit. »
 *
 * ── Pourquoi la confirmation n'est PAS un simple changement de statut ──────
 *
 * Jusqu'ici, « Confirmer » posait `rdvStatus: 'confirmed'` et rien d'autre. Or
 * le coup de fil est le SEUL moment où ces faits existent : le site ne connaît
 * qu'une date SOUHAITÉE et un créneau vague (« matin »), et le patient qui
 * décroche donne une heure, un lieu, et parfois une adresse. Ne pas les
 * enregistrer là revenait à les perdre — ils ne se retrouvaient ni dans la
 * demande, ni ailleurs, et le préleveur du lendemain lisait « souhaite le
 * 07/10, matin » sans savoir ni à quelle heure ni où aller.
 *
 * ── UN SEUL CHAMP, et non sept colonnes ────────────────────────────────────
 *
 * Ces faits naissent ENSEMBLE, au même appel, ou pas du tout : `confirmation`
 * est un champ `json` unique sur `api::appointment.appointment`. Sept colonnes
 * annulables auraient autorisé les demi-états — une heure sans lieu, une
 * adresse sans domicile — que rien n'aurait rattrapés, et qu'aucun écran
 * n'aurait su afficher. Même choix que `ged-nc.analyse`.
 *
 * ⚠️ CONSÉQUENCE ASSUMÉE : on ne peut pas trier ni filtrer en base sur l'heure
 * du rendez-vous. L'écran charge de toute façon toutes les demandes (voir
 * `lireToutes` dans `DemandesRdv.tsx`) et trie en mémoire ; le jour où « les
 * rendez-vous de demain » devra se demander au serveur, il faudra sortir
 * `date` et `heure` dans deux colonnes.
 *
 * ── Module PUR, et pourquoi ────────────────────────────────────────────────
 *
 * Aucun import d'exécution : il est compilé et éprouvé par
 * `backend/tests/front-rdv-confirmation.test.js`, la seule intégration
 * continue du dépôt. Les règles de saisie (ce qui est obligatoire, ce qui est
 * erroné, l'adresse qui n'est exigée QUE pour un domicile) vivent donc ici et
 * non dans le JSX, où rien ne les relirait.
 */

/** Les deux endroits possibles, et il n'y en a pas de troisième : le
 *  laboratoire n'a qu'une implantation (« Monastir — Ruspina »). Le jour où il
 *  y en aura deux, c'est `lieu` qui portera le site, pas une liste parallèle. */
export type LieuRdv = 'labo' | 'domicile';

export const LIBELLES_LIEU: Record<LieuRdv, string> = {
  labo: 'Au laboratoire',
  domicile: 'À domicile',
};

/**
 * Ce que le coup de fil a établi.
 *
 * `date` et `heure` sont le rendez-vous CONVENU, et non ce que le patient avait
 * demandé sur le site : un appel sert justement à déplacer une date. Les deux
 * coexistent donc sur la demande — `preferredDate` reste ce qui a été souhaité,
 * et l'écart entre les deux est une information en soi.
 */
export interface ConfirmationRdv {
  /** Patronyme seul, comme partout ailleurs (cf. `rh-personnel.nom`). */
  nom: string;
  prenom: string;
  /** Date de naissance, `AAAA-MM-JJ`. FACULTATIVE (arbitrage du 05/10/2026) :
   *  le patient ne l'a pas toujours sous la main au téléphone, et bloquer la
   *  confirmation pour ça aurait fait inventer des dates. */
  naissance?: string;
  /** Jour convenu, `AAAA-MM-JJ`. */
  date: string;
  /** Heure convenue, `HH:MM` sur 24 h. */
  heure: string;
  lieu: LieuRdv;
  /** Renseignée POUR UN DOMICILE SEULEMENT — voir `normaliser`. */
  adresse?: string;
}

/**
 * L'état du formulaire pendant la saisie — DISTINCT de ce qui part en base.
 *
 * ⚠️ `lieu` PEUT Y ÊTRE VIDE, et c'est tout l'intérêt du type. Un formulaire
 * qui démarrerait sur « Au laboratoire » enregistrerait ce lieu-là pour qui
 * oublie de poser la question : le défaut le plus probable deviendrait le
 * silencieux. En partant de rien, le lieu est RÉCLAMÉ comme l'heure — ce que la
 * demande nomme d'ailleurs ensemble, « surtout heure et endroit ».
 */
export interface SaisieConfirmation {
  nom: string;
  prenom: string;
  naissance: string;
  date: string;
  heure: string;
  lieu: LieuRdv | '';
  adresse: string;
}

/** Un champ tel que le formulaire le déclare. Forme STRUCTURELLE de
 *  `ChampDeclare` (`lib/formulaire.ts`), recopiée et non importée : ce module
 *  doit rester une feuille pour que le lanceur puisse le compiler seul. */
export interface ChampConfirmation {
  id: string;
  label: string;
  valeur: unknown;
  requis?: boolean;
  erreur?: string | null;
}

export const VIDE: SaisieConfirmation = { nom: '', prenom: '', naissance: '', date: '', heure: '', lieu: '', adresse: '' };

/**
 * Le formulaire au moment où il s'ouvre.
 *
 * Deux cas, et il ne faut pas les confondre : une demande DÉJÀ confirmée se
 * rouvre sur ce qui a été recueilli (on corrige un rendez-vous déplacé), une
 * demande neuve part de ce que le site sait — le nom découpé et la date
 * SOUHAITÉE.
 *
 * ⚠️ L'HEURE N'EST JAMAIS PRÉREMPLIE. Le site ne connaît qu'un créneau vague
 * (« matin ») ; en tirer « 08:00 » serait inventer la seule information que le
 * coup de fil sert à obtenir, et l'opérateur n'aurait plus aucune raison de la
 * demander.
 */
export function depart(
  d: { patientName?: string; preferredDate?: string; confirmation?: unknown },
): SaisieConfirmation {
  const c = d.confirmation;
  if (estConfirme(c)) {
    return {
      nom: net(c.nom), prenom: net(c.prenom), naissance: net(c.naissance),
      date: net(c.date), heure: net(c.heure), lieu: c.lieu, adresse: net(c.adresse),
    };
  }
  const { prenom, nom } = decouperNom(net(d.patientName));
  return { ...VIDE, prenom, nom, date: net(d.preferredDate).slice(0, 10) };
}

const net = (v: unknown) => String(v ?? '').trim();

/**
 * Découpe le nom unique du site en prénom + patronyme.
 *
 * ⚠️ C'EST UNE PROPOSITION, JAMAIS UNE VÉRITÉ. Le formulaire public ne
 * demande qu'un champ libre (`patientName`) ; aucune règle ne peut dire où
 * s'arrête un prénom composé. On prend donc la convention majoritaire des
 * données du laboratoire — premier mot = prénom, le reste = patronyme — et
 * l'écran présente le résultat EN SAISIE, à vérifier au téléphone. Le deviner
 * en silence et l'enregistrer sans le montrer serait la seule vraie faute.
 */
export function decouperNom(complet: string): { prenom: string; nom: string } {
  const mots = net(complet).split(/\s+/).filter(Boolean);
  if (mots.length === 0) return { prenom: '', nom: '' };
  if (mots.length === 1) return { prenom: '', nom: mots[0] as string };
  return { prenom: mots[0] as string, nom: mots.slice(1).join(' ') };
}

/** Une date `AAAA-MM-JJ` qui existe vraiment — le 31 février est refusé. */
export function dateValide(v: string): boolean {
  const s = net(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const [a, mo, j] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || j < 1 || j > 31) return false;
  const d = new Date(Date.UTC(a, mo - 1, j));
  return d.getUTCFullYear() === a && d.getUTCMonth() === mo - 1 && d.getUTCDate() === j;
}

/** Une heure `HH:MM` sur 24 h. `24:00` et `8:30` sont refusés — le champ
 *  `<input type="time">` ne les produit pas, mais une reprise en base peut. */
export function heureValide(v: string): boolean {
  const m = /^(\d{2}):(\d{2})$/.exec(net(v));
  if (!m) return false;
  return Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/**
 * Les champs du pop-up, DANS L'ORDRE D'AFFICHAGE.
 *
 * L'ordre n'est pas cosmétique : `useFormulaire` emmène au PREMIER problème de
 * cette liste, donc au plus haut à l'écran (cf. `lib/formulaire.ts`).
 *
 * `aujourdhui` est PASSÉ et non lu ici : un module pur ne lit pas l'horloge,
 * sinon son test ne vaut que le jour où il a été écrit.
 */
export function champsConfirmation(c: SaisieConfirmation, aujourdhui: string): ChampConfirmation[] {
  const domicile = c.lieu === 'domicile';
  return [
    { id: 'rdv-conf-prenom', label: 'Prénom', valeur: net(c.prenom), requis: true },
    { id: 'rdv-conf-nom', label: 'Nom', valeur: net(c.nom), requis: true },
    {
      id: 'rdv-conf-naissance', label: 'Date de naissance', valeur: net(c.naissance), requis: false,
      /* Facultative, mais pas libre : une DDN remplie doit être une date, et
         une date passée. Un patient né « demain » est une faute de frappe. */
      erreur: !net(c.naissance) ? null
        : !dateValide(c.naissance) ? 'Date impossible — attendu JJ/MM/AAAA'
          : (net(aujourdhui) && net(c.naissance) > net(aujourdhui)) ? 'Date de naissance dans le futur'
            : null,
    },
    {
      id: 'rdv-conf-date', label: 'Date du rendez-vous', valeur: net(c.date), requis: true,
      erreur: net(c.date) && !dateValide(c.date) ? 'Date impossible — attendu JJ/MM/AAAA' : null,
    },
    {
      id: 'rdv-conf-heure', label: 'Heure du rendez-vous', valeur: net(c.heure), requis: true,
      erreur: net(c.heure) && !heureValide(c.heure) ? 'Heure impossible — attendu HH:MM' : null,
    },
    { id: 'rdv-conf-lieu', label: 'Lieu du rendez-vous', valeur: c.lieu, requis: true },
    /* ⚠️ `requis` SUIT LE LIEU. Une adresse exigée en permanence aurait fait
       réclamer l'adresse de quelqu'un qui vient au laboratoire ; jamais exigée,
       elle aurait laissé partir un prélèvement à domicile sans destination.
       C'est la seule règle conditionnelle du formulaire, et elle est ici plutôt
       que dans le JSX pour qu'un test la relise. */
    {
      id: 'rdv-conf-adresse', label: domicile ? 'Adresse du domicile' : 'Adresse (inutile au laboratoire)',
      valeur: net(c.adresse), requis: domicile,
    },
  ];
}

/**
 * Ce qui part en base.
 *
 * ⚠️ L'ADRESSE EST EFFACÉE quand le lieu repasse au laboratoire. Sans ça, un
 * opérateur qui saisit une adresse puis corrige le lieu laisserait une adresse
 * orpheline dans l'enregistrement : la fiche dirait « au laboratoire » et
 * porterait pourtant une destination, et personne ne saurait laquelle croire.
 */
export function normaliser(c: SaisieConfirmation): ConfirmationRdv {
  const lieu: LieuRdv = c.lieu === 'domicile' ? 'domicile' : 'labo';
  const sortie: ConfirmationRdv = {
    nom: net(c.nom), prenom: net(c.prenom), date: net(c.date), heure: net(c.heure), lieu,
  };
  if (net(c.naissance)) sortie.naissance = net(c.naissance);
  if (lieu === 'domicile' && net(c.adresse)) sortie.adresse = net(c.adresse);
  return sortie;
}

/** Vrai si l'enregistrement porte une confirmation exploitable. Un objet
 *  présent mais sans heure ni lieu n'en est pas une : l'écran doit alors se
 *  taire plutôt qu'afficher « le — à — ». */
export function estConfirme(c: unknown): c is ConfirmationRdv {
  if (!c || typeof c !== 'object') return false;
  const o = c as Record<string, unknown>;
  return !!net(o.date) && !!net(o.heure) && (o.lieu === 'labo' || o.lieu === 'domicile');
}

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/**
 * Le jour en toutes lettres, JOUR DE LA SEMAINE COMPRIS.
 *
 * Le nom du jour n'est pas une coquetterie : c'est le seul contrôle de
 * vraisemblance que l'opérateur peut faire au téléphone. « 07/10/2025 » se lit
 * sans broncher ; « mardi 7 octobre 2025 » quand on est en 2026 se voit.
 */
export function jourEnClair(iso: string): string {
  if (!dateValide(iso)) return net(iso);
  const [a, m, j] = net(iso).split('-').map(Number) as [number, number, number];
  const d = new Date(Date.UTC(a, m - 1, j));
  return `${JOURS[d.getUTCDay()]} ${j} ${MOIS[m - 1]} ${a}`;
}

/** `08:30` → `08 h 30`, la forme française. Une heure illisible est rendue
 *  telle quelle : on ne maquille pas une valeur qu'on n'a pas comprise. */
export function heureEnClair(h: string): string {
  return heureValide(h) ? net(h).replace(':', ' h ') : net(h);
}

/**
 * La phrase que lit le préleveur. C'est elle qui porte l'information utile —
 * d'où le lieu en toutes lettres et l'adresse à la suite, et non une pastille
 * « domicile » qu'il faudrait cliquer pour savoir où aller.
 */
export function resumeConfirmation(c: unknown): string {
  if (!estConfirme(c)) return '';
  const lieu = c.lieu === 'domicile'
    ? `à domicile${net(c.adresse) ? ` — ${net(c.adresse)}` : ''}`
    : 'au laboratoire';
  return `${jourEnClair(c.date)} à ${heureEnClair(c.heure)}, ${lieu}`;
}

/** L'identité recueillie, composée comme partout : « Prénom NOM ». Vide si le
 *  coup de fil n'a rien établi. */
export function identiteConfirmee(c: unknown): string {
  if (!c || typeof c !== 'object') return '';
  const o = c as Record<string, unknown>;
  return [net(o.prenom), net(o.nom)].filter(Boolean).join(' ');
}
